import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  FieldHint,
  RequiredMark,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import { ChipMultiSelect } from '@/client/pages/Admin/shared/ChipMultiSelect';
import type { SocialPlatform, SourceTier } from '@/client/types/media';
import { ALL_SOCIAL_PLATFORMS, PLATFORM_LABEL } from '@/client/types/media';
import type { ApiSourceAccount } from '@/client/types/marketing/api';
import {
  MEDIA_TOPIC_LABEL,
  MEDIA_TOPICS,
  type MediaTopic,
} from '@/client/types/marketing/strategy';
import { PlatformLogoBadge } from './PlatformLogo';
import {
  useCreateSourceAccountMutation,
  useUpdateSourceAccountMutation,
} from '@/client/api/marketing/sources';
import { getApiErrorMessage } from '@/client/utils/errors';

/**
 * Add or edit a source account, writing to
 * `/api/admin/v2/marketing/sources/accounts`.
 *
 * Booleans go out as 0/1: the columns are TINYINT, and sending the shape the
 * column actually holds keeps the round-trip symmetrical — an account read back
 * looks exactly like one just written.
 *
 * `verify_state` is not editable here. Whether a handle exists is something the
 * platform answers, not something an operator asserts; use Verify on the row.
 */

const PLATFORMS: SocialPlatform[] = [...ALL_SOCIAL_PLATFORMS];

type Ownership = 'owned' | 'partner_influencer' | 'competitor' | 'other';

const OWNERSHIP_OPTIONS: { value: Ownership; label: string }[] = [
  { value: 'owned', label: 'Owned' },
  { value: 'partner_influencer', label: 'Partner influencer' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'other', label: 'Other' },
];

interface AccountFormDialogProps {
  open: boolean;
  account: ApiSourceAccount | null;
  onClose: () => void;
}

type FormState = {
  platform: SocialPlatform;
  handle: string;
  displayName: string;
  ownership: Ownership;
  useAsDestination: boolean;
  useAsListen: boolean;
  active: boolean;
  tier: SourceTier;
  followers: string;
  verified: boolean;
  region: string;
  topics: MediaTopic[];
  ownerName: string;
  notes: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const TOPIC_CHIP_OPTIONS = MEDIA_TOPICS.map((t) => ({
  value: t,
  label: MEDIA_TOPIC_LABEL[t],
}));

const knownTopics = (value: unknown): MediaTopic[] => {
  const raw = Array.isArray(value) ? (value as unknown[]).map(String) : [];
  return raw.filter((t): t is MediaTopic => (MEDIA_TOPICS as readonly string[]).includes(t));
};

function toForm(account: ApiSourceAccount | null): FormState {
  if (!account) {
    return {
      platform: 'instagram',
      handle: '',
      displayName: '',
      ownership: 'owned',
      useAsDestination: true,
      useAsListen: true,
      active: true,
      tier: 2,
      followers: '',
      verified: false,
      region: '',
      topics: [],
      ownerName: '',
      notes: '',
    };
  }
  const ownership = (account.ownership as Ownership) || (account.owned ? 'owned' : 'other');
  const topics = Array.isArray(account.topics) ? account.topics : [];
  return {
    platform: account.platform as SocialPlatform,
    handle: account.handle,
    displayName: account.display_name,
    ownership,
    useAsDestination: !!account.use_as_destination,
    useAsListen: !!account.use_as_listen,
    active: !!account.active,
    tier: (account.tier as SourceTier) ?? 2,
    followers: account.followers != null ? String(account.followers) : '',
    verified: !!account.verified,
    region: account.region ?? '',
    topics: knownTopics(topics),
    ownerName: account.owner_name ?? '',
    notes: account.notes ?? '',
  };
}

function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

export function AccountFormDialog({ open, account, onClose }: AccountFormDialogProps) {
  const isEdit = !!account;
  const [form, setForm] = useState<FormState>(() => toForm(account));
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const createAccount = useCreateSourceAccountMutation();
  const updateAccount = useUpdateSourceAccountMutation();
  const saving = createAccount.isPending || updateAccount.isPending;

  useEffect(() => {
    if (open) {
      setForm(toForm(account));
      setFormErrors({});
    }
  }, [open, account]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((e) => clearFieldError(e, key));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FormErrors = {};
    if (!form.displayName.trim()) {
      errors.displayName = 'Display name is required';
    }
    if (!form.handle.trim()) {
      errors.handle = 'Handle is required';
    }

    const followersRaw = form.followers.trim().replace(/,/g, '');
    const followers = followersRaw === '' ? undefined : Number(followersRaw);
    if (followers != null && (Number.isNaN(followers) || followers < 0)) {
      errors.followers = 'Followers must be a non-negative number';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Fix the highlighted fields before saving');
      return;
    }

    const owned = form.ownership === 'owned';
    // TINYINT columns: send 0/1, not true/false.
    const payload = {
      platform: form.platform,
      handle: form.handle.trim(),
      display_name: form.displayName.trim(),
      owned: owned ? 1 : 0,
      ownership: form.ownership,
      use_as_destination: form.useAsDestination ? 1 : 0,
      use_as_listen: form.useAsListen ? 1 : 0,
      active: form.active ? 1 : 0,
      tier: form.tier,
      followers: followers ?? null,
      verified: form.verified ? 1 : 0,
      region: form.region.trim() || null,
      topics: form.topics,
      owner_name: form.ownerName.trim() || null,
      notes: form.notes.trim() || null,
    };

    const handlers = {
      onSuccess: () => {
        toast.success(isEdit ? 'Account updated' : 'Account added');
        onClose();
      },
      onError: (err: unknown) =>
        toast.error(getApiErrorMessage(err, 'Could not save the account')),
    };

    if (isEdit && account) {
      updateAccount.mutate({ said: account.said, ...payload }, handlers);
    } else {
      createAccount.mutate(payload, handlers);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlatformLogoBadge platform={form.platform} />
            {isEdit ? 'Edit account' : 'Add account'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update this owned or tracked social account.'
              : 'Track a new owned brand account or third-party handle.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Platform">
              <select
                value={form.platform}
                onChange={(e) => set('platform', e.target.value as SocialPlatform)}
                className={inputClass}
              >
                {PLATFORMS.map((p) => (
                  <option key={p} value={p}>
                    {PLATFORM_LABEL[p]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ownership">
              <select
                value={form.ownership}
                onChange={(e) => {
                  const ownership = e.target.value as Ownership;
                  const owned = ownership === 'owned';
                  setForm((prev) => ({
                    ...prev,
                    ownership,
                    useAsDestination: owned ? true : prev.useAsDestination,
                  }));
                }}
                className={inputClass}
              >
                {OWNERSHIP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={<><span>Display name</span> <RequiredMark /></>}>
              <input
                value={form.displayName}
                onChange={(e) => set('displayName', e.target.value)}
                className={fieldClass(inputClass, formErrors.displayName)}
                aria-invalid={!!formErrors.displayName}
                placeholder="Fairful"
              />
              <FieldHint error={formErrors.displayName} />
            </Field>
            <Field label={<><span>Handle</span> <RequiredMark /></>}>
              <input
                value={form.handle}
                onChange={(e) => set('handle', e.target.value)}
                className={fieldClass(inputClass, formErrors.handle)}
                aria-invalid={!!formErrors.handle}
                placeholder="@fairful"
              />
              <FieldHint error={formErrors.handle} />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Tier">
              <select
                value={form.tier}
                onChange={(e) => set('tier', Number(e.target.value) as SourceTier)}
                className={inputClass}
              >
                <option value={1}>Tier 1</option>
                <option value={2}>Tier 2</option>
                <option value={3}>Tier 3</option>
              </select>
            </Field>
            <Field label="Followers">
              <input
                value={form.followers}
                onChange={(e) => set('followers', e.target.value)}
                className={fieldClass(inputClass, formErrors.followers)}
                aria-invalid={!!formErrors.followers}
                placeholder="128400"
                inputMode="numeric"
              />
              <FieldHint error={formErrors.followers} />
            </Field>
            <Field label="Region">
              <input
                value={form.region}
                onChange={(e) => set('region', e.target.value)}
                className={inputClass}
                placeholder="GH / Global"
              />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Owner (internal)">
              <input
                value={form.ownerName}
                onChange={(e) => set('ownerName', e.target.value)}
                className={inputClass}
                placeholder="Brand / Social / Comms"
              />
            </Field>
            <Field label="Topics">
              <ChipMultiSelect
                options={TOPIC_CHIP_OPTIONS}
                value={form.topics}
                onChange={(next) => set('topics', next)}
                aria-label="Account topics"
              />
            </Field>
          </div>

          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="min-h-[72px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
              placeholder="Relationship notes, pitch guidance…"
            />
          </Field>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.useAsDestination}
                onChange={(e) => set('useAsDestination', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Use as destination
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.useAsListen}
                onChange={(e) => set('useAsListen', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Use as listen
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.verified}
                onChange={(e) => set('verified', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Verified badge
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
